import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, CalendarClock, ChartColumn, ChevronLeft, ChevronRight, CircleCheck, CircleDot, Hourglass, Plus, StickyNote } from 'lucide-react';
import { keyFor } from '@missions/api-client';
import type { MissionSummary, Project } from '@missions/types';
import { toISODate } from '@missions/ui';
import { api } from '../../lib/client';
import { useErrorToast } from '../../lib/hooks';
import { useSession, useT } from '../../lib/session';
import { AvStack, Due, Empty, Pri, SDot, Tag } from '../../components/atoms';
import { useShell } from '../../layout/shell-context';
import { celebrate, useMissionInvalidate } from '../../features/missions/data';
import { type ListState, filterJson } from './useProjectData';

/** Board: status columns with drag & drop (optimistic move). */
export function BoardView({ project, state }: { project: Project; state: ListState }) {
  const t = useT();
  const s = useSession();
  const shell = useShell();
  const qc = useQueryClient();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const base = useMemo(() => ({ projectId: project.id, groupBy: 'status' as const, sort: state.sort, q: state.q || undefined, filter: filterJson({ ...state, showClosed: true }), limit: 25 }), [project.id, state]);
  const key = keyFor('missions.groups', { query: base });
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => api.call('missions.groups', { query: base, signal }) });
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const canEdit = s.can('mission.edit');

  const move = async (missionId: string, statusId: string, el: Element | null) => {
    const prev = qc.getQueryData(key);
    const to = project.statuses.find((x) => x.id === statusId);
    qc.setQueryData(key, (old: typeof q.data) => {
      if (!old) return old;
      const m = old.groups.flatMap((g) => g.items).find((x) => x.id === missionId);
      if (!m) return old;
      return {
        groups: old.groups.map((g) =>
          g.value === statusId
            ? { ...g, count: g.count + (m.statusId === statusId ? 0 : 1), items: [{ ...m, statusId }, ...g.items.filter((x) => x.id !== missionId)] }
            : { ...g, count: g.count - (g.items.some((x) => x.id === missionId) && m.statusId !== statusId ? 1 : 0), items: g.items.filter((x) => x.id !== missionId) },
        ),
      };
    });
    try {
      await api.call('missions.move', { params: { id: missionId }, body: { statusId } });
      if (to?.isClosed && el) celebrate(el);
      void refresh();
    } catch (e) {
      qc.setQueryData(key, prev);
      onErr(e);
    }
  };

  if (q.isPending)
    return (
      <div className="board">
        {project.statuses.map((st) => (
          <div className="bcol" key={st.id}>
            <div className="bcol-h"><span className="spill" style={{ background: st.color }}>{st.name}</span></div>
            {[0, 1, 2].map((i) => <div className="bcard" key={i}><span className="sk ui-skel" style={{ width: '80%', marginBottom: 10, display: 'block' }} /><span className="sk ui-skel" style={{ width: '40%', display: 'block' }} /></div>)}
          </div>
        ))}
      </div>
    );
  return (
    <div className="board">
      {(q.data?.groups ?? []).map((g) => (
        <div
          className={`bcol${over === g.value ? ' over' : ''}`}
          key={g.value}
          onDragOver={(e) => {
            if (!drag) return;
            e.preventDefault();
            setOver(g.value);
          }}
          onDragLeave={() => setOver((o) => (o === g.value ? null : o))}
          onDrop={(e) => {
            e.preventDefault();
            setOver(null);
            if (drag) void move(drag, g.value, e.currentTarget);
            setDrag(null);
          }}
          aria-label={`${g.label} column`}
        >
          <div className="bcol-h">
            <span className="spill" style={{ background: g.color ?? 'var(--text-3)' }}>{g.isClosed ? t.closedGroup : g.label}</span>
            <span className="gcount">{g.count}</span>
          </div>
          {g.items.map((m) => (
            <button
              key={m.id}
              className={`bcard${drag === m.id ? ' dragging' : ''}`}
              draggable={canEdit}
              onDragStart={(e) => {
                setDrag(m.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', m.id);
              }}
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
              onClick={() => shell.openMission(m.id)}
            >
              <div className="t"><span className="tid">{m.key}</span>{m.title}</div>
              <div className="m">
                {m.tags.map((x) => <Tag key={x.id} name={x.name} color={x.color} />)}
                <Due due={m.dueAt} done={!!m.completedAt} />
                <Pri p={m.priority} compact />
                <span style={{ marginLeft: 'auto' }}><AvStack users={m.assignees} size={22} max={3} /></span>
              </div>
            </button>
          ))}
          {g.count > g.items.length && <div className="gfoot" style={{ padding: '6px 4px' }}>+{g.count - g.items.length} more in List view</div>}
          {s.can('mission.create') && (
            <button className="bc-add" onClick={() => shell.openNewMission({ projectId: project.id, statusId: g.value })} aria-label={`${t.addItem} to ${g.label}`}>
              <Plus />{t.addItem}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Calendar: month grid of due dates; "+" on a day creates a mission due that day. */
export function CalendarView({ project, state }: { project: Project; state: ListState }) {
  const t = useT();
  const s = useSession();
  const shell = useShell();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const start = new Date(month);
  start.setDate(1 - ((month.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  const from = toISODate(days[0]!);
  const to = toISODate(days[41]!);
  const query = { projectId: project.id, groupBy: 'none' as const, sort: 'dueAt' as const, from, to, q: state.q || undefined, filter: filterJson(state), limit: 100 };
  const q = useQuery({ queryKey: keyFor('missions.list', { query }), queryFn: ({ signal }) => api.call('missions.list', { query, signal }) });
  const byDay = useMemo(() => {
    const m = new Map<string, MissionSummary[]>();
    for (const x of q.data?.data ?? []) if (x.dueAt) m.set(x.dueAt, [...(m.get(x.dueAt) ?? []), x]);
    return m;
  }, [q.data]);
  const todayIso = toISODate(new Date());
  return (
    <>
      <div className="cal-h">
        <button className="ib" aria-label="Previous month" data-tooltip="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft /></button>
        <b>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</b>
        <button className="ib" aria-label="Next month" data-tooltip="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight /></button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-o sm" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</button>
      </div>
      <div className="cal-g">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div className="cal-dow" key={d}>{d}</div>)}
        {days.map((d) => {
          const iso = toISODate(d);
          const ev = byDay.get(iso) ?? [];
          return (
            <div key={iso} className={`cal-d${d.getMonth() !== month.getMonth() ? ' out' : ''}${iso === todayIso ? ' today' : ''}`}>
              <span className="n">{d.getDate()}</span>
              {s.can('mission.create') && (
                <button className="cal-add" onClick={() => shell.openNewMission({ projectId: project.id, dueAt: iso })} aria-label={`${t.addItem} on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`} data-tooltip={t.addItem}>
                  <Plus />
                </button>
              )}
              {ev.slice(0, 3).map((m) => (
                <button key={m.id} className="cev" style={{ borderColor: project.statuses.find((x) => x.id === m.statusId)?.color }} data-tooltip={m.title} onClick={() => shell.openMission(m.id)}>
                  {m.title}
                </button>
              ))}
              {ev.length > 3 && <span className="cal-more">+{ev.length - 3} more</span>}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Overview: open / overdue / progress / ETA tiles, status breakdown, upcoming deadlines. */
export function OverviewView({ project }: { project: Project }) {
  const t = useT();
  const shell = useShell();
  const groups = useQuery({ queryKey: keyFor('missions.groups', { query: { projectId: project.id, groupBy: 'status' } }), queryFn: ({ signal }) => api.call('missions.groups', { query: { projectId: project.id, groupBy: 'status', limit: 5 }, signal }) });
  const upcomingQ = { projectId: project.id, groupBy: 'none' as const, sort: 'dueAt' as const, limit: 5, filter: JSON.stringify({ match: 'all', rules: [], quick: { showClosed: false } }) };
  const upcoming = useQuery({ queryKey: keyFor('missions.list', { query: upcomingQ }), queryFn: ({ signal }) => api.call('missions.list', { query: upcomingQ, signal }) });
  const total = (groups.data?.groups ?? []).reduce((a, g) => a + g.count, 0) || 1;
  const tile = (icon: React.ReactNode, c: string, k: string, v: string | number, d: string) => (
    <div className="stat" key={k}>
      <div className="k"><span className="ic" style={{ background: `color-mix(in srgb,${c} 14%,var(--surface))`, color: c }}>{icon}</span>{k}</div>
      <div className="v">{v}</div>
      <div className="d">{d}</div>
    </div>
  );
  return (
    <div className="card-b">
      <div className="ov-grid">
        {tile(<CircleDot />, '#0457fd', `Open ${t.items}`, project.counts.open, `of ${project.counts.total} total`)}
        {tile(<AlarmClock />, '#e5484d', 'Overdue', project.counts.overdue, 'needs attention')}
        {tile(<CircleCheck />, '#16a34a', 'Completed', `${Math.round(project.progress)}%`, 'project progress')}
        {tile(<Hourglass />, '#e59400', 'Done', project.counts.done, `${t.items} accomplished`)}
      </div>
      <div className="two">
        <div className="card">
          <div className="card-h"><span className="tile"><ChartColumn /></span><h2>Status breakdown</h2></div>
          <div className="card-b brk">
            {(groups.data?.groups ?? []).map((g) => (
              <div className="r" key={g.value}>
                <span>{g.label}</span>
                <span className="pbar"><i style={{ width: `${Math.round((g.count / total) * 100)}%`, background: g.color ?? 'var(--brand)' }} /></span>
                <b>{g.count}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-h"><span className="tile"><CalendarClock /></span><h2>Upcoming deadlines</h2></div>
          <div className="card-b" style={{ paddingTop: 4, paddingBottom: 4 }}>
            {(upcoming.data?.data ?? []).map((m) => (
              <button className="lrow" key={m.id} onClick={() => shell.openMission(m.id)}>
                <SDot s={project.statuses.find((x) => x.id === m.statusId)} />
                <span className="nm">{m.title}<small>{m.assignees.map((a) => a.name).join(', ') || 'Unassigned'}</small></span>
                <Due due={m.dueAt} />
              </button>
            ))}
            {upcoming.data && !upcoming.data.data.length && <div style={{ padding: '14px 0', color: 'var(--text-3)' }}>No upcoming deadlines.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function StickiesPreview({ project }: { project: Project }) {
  const nav = useNavigate();
  const shell = useShell();
  const boards = useQuery({ queryKey: keyFor('boards.list', { query: { workspaceId: shell.workspaceId } }), queryFn: ({ signal }) => api.call('boards.list', { query: { workspaceId: shell.workspaceId }, signal }) });
  const mine = (boards.data?.data ?? []).filter((b) => b.projectId === project.id);
  return (
    <div className="card-b">
      <div className="view-note" style={{ marginBottom: 12 }}><StickyNote />Sticky boards for this project. Open one to drag notes, change colours and turn notes into missions.</div>
      {mine.length ? (
        <div className="missions">
          {mine.map((b) => (
            <button className="mcard" key={b.id} onClick={() => nav(`/boards/${b.id}`)}>
              <div className="top"><div className="nm"><b>{b.name}</b><small>{b.stickyCount} stickies</small></div></div>
            </button>
          ))}
        </div>
      ) : (
        <Empty icon={<StickyNote />} title="No sticky boards yet" text="Create one to brainstorm with your crew." actions={<button className="btn btn-p" onClick={() => nav('/boards')}><Plus />Open sticky boards</button>} />
      )}
    </div>
  );
}
